import { styled } from '@mui/material';

import { useI18n } from '@app-velocity/i18n';
import { PreviewHighlightProvider } from '@app-velocity/common';
import { Typography } from '@app-velocity/ui';

import { useReportContext } from '../../context/useReportContext';
import { PreviewNavigation } from '../PreviewNavigation';
import { ViewPreview } from '../ViewPreview';
import { SinglePathView } from '../../types';

export type PathRunStepDetailsProps = {
  view: SinglePathView;
  persona: 'accessible' | 'default' | string;
  stepNumber: number;
  reviewId?: string;
  onPrevious?: () => void;
  onNext?: () => void;
};

const StyledPanelContent = styled('div')(({ theme }) => ({
  padding: `0 ${theme.spacing(4)}`,
}));

const StyledLayout = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(6),
  width: '100%',
  maxWidth: 1200,
  minHeight: theme.spacing(120),
  margin: '0 auto',
  padding: `0 ${theme.spacing(8)}`,
  [theme.breakpoints.up('lg')]: {
    display: 'grid',
    gridTemplateColumns: '1fr 0.75fr',
  },
}));

const StyledPreviewColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const StyledExplanationColumn = styled('div')(({ theme }) => ({
  flex: '1 1 0',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 700,
  marginBottom: theme.spacing(4),
}));

export const PathRunStepDetails = ({
  view,
  persona,
  stepNumber,
  reviewId,
  onPrevious,
  onNext,
}: PathRunStepDetailsProps) => {
  const { t } = useI18n();
  const { renderRunStepExplanation } = useReportContext();

  const hasPreview = !!(view.pageId || view.image);

  if (!hasPreview && !renderRunStepExplanation) return null;

  const pageName = view.name;

  const content = (
    <PreviewHighlightProvider
      viewportWidth={view.viewportWidth}
      viewportHeight={view.viewportHeight}
    >
      <StyledPanelContent>
        <StyledTitle component="h3">
          {t('prototypes:step_n', { number: stepNumber + 1 })}
          {pageName ? ` - ${pageName}` : ''}
        </StyledTitle>
        <StyledLayout>
          {hasPreview ? (
            <StyledPreviewColumn>
              <ViewPreview
                highlight
                persona={persona}
                size="large"
                pageId={view.pageId || ''}
                partials={view.partials}
                clicks={view.clicks}
                image={view.image}
                width={view.width}
                height={view.height}
              />
            </StyledPreviewColumn>
          ) : null}
          {renderRunStepExplanation ? (
            <StyledExplanationColumn>
              {renderRunStepExplanation({ view, stepNumber, reviewId })}
            </StyledExplanationColumn>
          ) : null}
        </StyledLayout>
      </StyledPanelContent>
    </PreviewHighlightProvider>
  );

  if (onPrevious && onNext) {
    return (
      <PreviewNavigation onPrevious={onPrevious} onNext={onNext}>
        {content}
      </PreviewNavigation>
    );
  }

  return content;
};
